import {
  DetailCard,
  DetailIconBadge,
  type DetailCardAccent,
  type DetailIconBadgeProps,
  type DetailIconTone,
} from '@/components/patterns/detail-page'
import { Button, type ButtonProps } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import type { ComponentProps, ComponentType, ReactNode } from 'react'

type AdminPageIcon = ComponentType<{ className?: string }>

const pageShellMaxWidthClasses = {
  full: 'max-w-none',
  wide: 'max-w-7xl',
  content: 'max-w-5xl',
  form: 'max-w-4xl',
  narrow: 'max-w-2xl',
}

const softPageHeaderClass = 'px-1 py-2 sm:px-2'

const pageHeaderDensityClasses = {
  default: softPageHeaderClass,
  compact: 'rounded-xl p-3 sm:p-4',
}

const recordHeaderLayoutClasses = {
  default: {
    content: 'space-y-5 p-5',
    header: '',
  },
  workspace: {
    content: 'p-0',
    header: 'border-b border-border/50 px-5 py-4',
  },
}

export type AdminPageShellMaxWidth = keyof typeof pageShellMaxWidthClasses

export type AdminPageShellProps = ComponentProps<'div'> & {
  maxWidth?: AdminPageShellMaxWidth
  withRightGutter?: boolean
}

export function AdminPageShell({
  children,
  className,
  maxWidth = 'full',
  withRightGutter = false,
  ...props
}: AdminPageShellProps) {
  return (
    <div
      className={cn(
        'w-full space-y-4 pb-10',
        pageShellMaxWidthClasses[maxWidth] ?? pageShellMaxWidthClasses.full,
        withRightGutter && '-mr-4 pr-6',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export type AdminPageHeaderDensity = keyof typeof pageHeaderDensityClasses

export type AdminPageHeaderProps = {
  icon?: AdminPageIcon
  title?: ReactNode
  description?: ReactNode
  eyebrow?: ReactNode
  badges?: ReactNode
  actions?: ReactNode
  metrics?: ReactNode
  density?: AdminPageHeaderDensity
  className?: string
  innerClassName?: string
  actionsClassName?: string
}

export function AdminPageHeader({
  icon: Icon,
  title,
  description,
  eyebrow,
  badges,
  actions,
  metrics,
  density = 'default',
  className,
  innerClassName,
  actionsClassName,
}: AdminPageHeaderProps) {
  const isCompact = density === 'compact'

  return (
    <header
      data-slot="admin-page-header"
      data-density={density}
      className={cn(
        'w-full @container/admin-page-header',
        pageHeaderDensityClasses[density],
        className
      )}
    >
      {/* Actions share the title's row only when they fit beside the unwrapped title and a
          readable (20rem) title column; otherwise they wrap to their own row. */}
      <div className={cn('flex flex-wrap items-center justify-between gap-4', innerClassName)}>
        <div className="min-w-[min(100%,20rem)] flex-[1_1_auto] space-y-2">
          <div className="flex min-w-0 items-center gap-3">
            {Icon ? (
              <span
                className={cn(
                  'inline-flex size-12 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-interaction',
                  isCompact && 'size-10'
                )}
              >
                <Icon className={isCompact ? 'size-5' : 'size-6'} />
              </span>
            ) : null}
            <div className="min-w-0">
              {eyebrow ? <Typography variant="eyebrow">{eyebrow}</Typography> : null}
              <Typography as="h1" variant={isCompact ? 'h3' : 'pageTitle'} className="pb-0">
                {title}
              </Typography>
              {badges ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">{badges}</div>
              ) : null}
            </div>
          </div>
          {/* Description and metrics fill whatever width the title row leaves; they never
              widen the header, so they cannot push the actions onto their own row. */}
          {description ? (
            <div className="w-0 min-w-full">
              <Typography variant="description" className="max-w-3xl">
                {description}
              </Typography>
            </div>
          ) : null}
          {metrics ? (
            <div className="flex w-0 min-w-full flex-wrap items-center gap-2">{metrics}</div>
          ) : null}
        </div>
        {actions ? (
          <div
            data-slot="admin-page-header-actions"
            className={cn('flex flex-wrap items-end gap-2', actionsClassName)}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  )
}

export type AdminHeaderActionButtonProps = ButtonProps

export function AdminHeaderActionButton({
  className,
  size = 'default',
  variant = 'default',
  ...props
}: AdminHeaderActionButtonProps) {
  const isIcon = size === 'icon'
  const resolvedVariant = variant === 'outline' ? 'outlineCard' : variant

  return (
    <Button
      size={isIcon ? 'icon' : 'header'}
      variant={resolvedVariant}
      className={className}
      {...props}
    />
  )
}

export type AdminHeaderIconButtonProps = AdminHeaderActionButtonProps & {
  icon?: AdminPageIcon
  label?: string
}

export function AdminHeaderIconButton({
  icon: Icon,
  label,
  className,
  children,
  ...props
}: AdminHeaderIconButtonProps) {
  return (
    <AdminHeaderActionButton
      type="button"
      variant="outline"
      size="icon"
      className={className}
      aria-label={label}
      {...props}
    >
      {Icon ? <Icon className="size-4" /> : children}
      {label ? <span className="sr-only">{label}</span> : null}
    </AdminHeaderActionButton>
  )
}

export type AdminDetailGridProps = {
  children?: ReactNode
  main?: ReactNode
  aside?: ReactNode
  className?: string
  asideClassName?: string
  asideFirstOnStack?: boolean
}

export function AdminDetailGrid({
  children,
  main,
  aside,
  className,
  asideClassName,
  asideFirstOnStack = false,
}: AdminDetailGridProps) {
  return (
    <div
      className={cn(
        'grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)] xl:items-start',
        className
      )}
    >
      {children ?? (
        <>
          <div className="min-w-0 space-y-5">{main}</div>
          <aside
            className={cn(
              'grid min-w-0 gap-4 lg:grid-cols-2 xl:sticky xl:top-1 xl:block xl:space-y-4',
              asideFirstOnStack && 'order-first xl:order-none',
              asideClassName
            )}
          >
            {aside}
          </aside>
        </>
      )}
    </div>
  )
}

export type AdminRecordHeaderLayout = keyof typeof recordHeaderLayoutClasses

export type AdminRecordHeaderProps = {
  icon?: DetailIconBadgeProps['icon']
  iconTone?: DetailIconTone
  title?: ReactNode
  titleTrailing?: ReactNode
  subtitle?: ReactNode
  meta?: ReactNode
  badges?: ReactNode
  actions?: ReactNode
  secondaryActions?: ReactNode
  children?: ReactNode
  after?: ReactNode
  className?: string
  layout?: AdminRecordHeaderLayout
}

export function AdminRecordHeader({
  icon,
  iconTone = 'neutral',
  title,
  titleTrailing,
  subtitle,
  meta,
  badges,
  actions,
  secondaryActions,
  children,
  after,
  className,
  layout = 'default',
}: AdminRecordHeaderProps) {
  const layoutClasses = recordHeaderLayoutClasses[layout] ?? recordHeaderLayoutClasses.default

  return (
    <DetailCard
      accent="hero"
      className={className}
      contentClassName={layoutClasses.content}
      data-slot="admin-record-header"
      data-layout={layout}
    >
      <div className={layoutClasses.header}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 gap-4">
            {icon ? (
              <DetailIconBadge
                icon={icon}
                tone={iconTone}
                className="size-8 pt-1"
                iconClassName="size-6"
              />
            ) : null}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <Typography variant="pageTitle" className="pb-0 lg:text-[2.35rem]">
                  {title}
                </Typography>
                {titleTrailing}
              </div>
              {subtitle ? (
                <Typography variant="body" tone="muted" className="mt-2">
                  {subtitle}
                </Typography>
              ) : null}
              {meta}
              {badges ? (
                <div className="mt-3 flex flex-wrap items-center gap-2">{badges}</div>
              ) : null}
              {children}
            </div>
          </div>
          {actions ? (
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">{actions}</div>
          ) : null}
        </div>

        {secondaryActions ? (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border/50 pt-4">
            {secondaryActions}
          </div>
        ) : null}
      </div>
      {after}
    </DetailCard>
  )
}

export type AdminSectionHeaderProps = {
  icon?: DetailIconBadgeProps['icon']
  iconTone?: DetailIconTone
  title?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  className?: string
}

export function AdminSectionHeader({
  icon,
  iconTone = 'neutral',
  title,
  description,
  actions,
  className,
}: AdminSectionHeaderProps) {
  return (
    <div className={cn('flex items-start justify-between gap-3', className)}>
      <div className="flex min-w-0 items-center gap-3">
        {icon ? <DetailIconBadge icon={icon} tone={iconTone} /> : null}
        <div className="min-w-0">
          <Typography variant="sectionTitle" className="pb-0">
            {title}
          </Typography>
          {description ? (
            <Typography variant="description" className="mt-1">
              {description}
            </Typography>
          ) : null}
        </div>
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  )
}

export type AdminSectionCardProps = AdminSectionHeaderProps & {
  children?: ReactNode
  accent?: DetailCardAccent
  contentClassName?: string
}

export function AdminSectionCard({
  icon,
  iconTone = 'neutral',
  title,
  description,
  actions,
  children,
  accent = 'neutral',
  className,
  contentClassName = 'space-y-4 p-5',
}: AdminSectionCardProps) {
  return (
    <DetailCard accent={accent} className={className} contentClassName={contentClassName}>
      <AdminSectionHeader
        icon={icon}
        iconTone={iconTone}
        title={title}
        description={description}
        actions={actions}
      />
      {children}
    </DetailCard>
  )
}

export type IconActionButtonProps = ButtonProps & {
  icon?: AdminPageIcon
  label?: string
}

export function IconActionButton({
  icon: Icon,
  label,
  className,
  children,
  ...props
}: IconActionButtonProps) {
  return (
    <Button
      type="button"
      variant="outline"
      size="icon-sm"
      className={cn('shrink-0', className)}
      aria-label={label}
      {...props}
    >
      {Icon ? <Icon className="size-4" /> : children}
      {label ? <span className="sr-only">{label}</span> : null}
    </Button>
  )
}
